import {
  Alert,
  Button,
  Card,
  Modal,
  ModalBody,
  ModalHeader,
} from "flowbite-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { base64ToBlob, isValidCNPJ, isValidCPF } from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { ADMIN, QUERIES } from "../../config/apiConfig";
import { HiInformationCircle } from "react-icons/hi";
import HeaderQuery from "./HeaderQuery";
import usePDF from "../../hooks/usePDF";
import CardQueryDescription from "../common/CardQueryDescription";
import SliderToPreview from "../common/SliderToPreview";
import QueryLoading from "../common/QueryLoading";
import QueryInput from "../inputs/QueryInput";
import { useQuery } from "react-query";
import DrawerLoading from "../DrawerLoading";
import LocalizacaoPFQuery from "./custom-queries-credit/LocalizacaoPFQuery";
import LocalizacaoPJQuery from "./custom-queries-credit/LocalizacaoPJQuery";
import TitleStripe from "../common/TitleStripe";
import ScrBacenQuery from "./custom-queries-credit/ScrBacenQuery";
import { useReactToPrint } from "react-to-print";
import { FaPrint } from "react-icons/fa6";
import AvisoCard from "../common/query/AvisoCard";
import moment from "moment";
import CadastralPFQuery from "./custom-queries-cadastral/CadastralPFQuery";
import CadastralPJQuery from "./custom-queries-cadastral/CadastralPJQuery";
import PreparePrint from "../common/PreparePrint";
import FooterLGPD from "../common/query/FooterLGPD";
import ErrorQuery from "../common/query/ErrorQuery";
import ParticipacaoEmEmpresas from "../common/query/ParticipacaoEmEmpresas";
import ParticipacaoEmEmpresasQuery from "./custom-queries-cadastral/ParticipacaoEmEmpresasQuery";
import CadastralPremiumPFQuery from "./custom-queries-cadastral/CadastralPremiumPFQuery";
import CadastralPremiumPJQuery from "./custom-queries-cadastral/CadastralPremiumPJQuery";
import { FaRegFilePdf } from "react-icons/fa";
import ConsultaLoading from "../ConsultaLoading";
import CadastralCompletoPFValida from "./custom-queries-cadastral/CadastralCompletoPFValida";

const CustomCadastralQuery = ({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = "true",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [isDoneRequest, setIsDoneRequest] = useState(false);
  const [error, setError] = useState("");
  const [errorQuery, setErrorQuery] = useState(0);
  const [data, setData] = useState(null);
  const [document, setDocument] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);
  const componentRef = useRef();
  const printRef = useRef();
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  /* abre modal automaticamente ao concluir consulta no mobile */
  useEffect(() => {
    if (isMobile && isDoneRequest && (data || errorQuery)) {
      setShowMobileModal(true);
    }
  }, [isMobile, isDoneRequest, data, errorQuery]);

  const handleAfterPrint = useCallback(() => {
    setIsPrinting(false);
  }, []);

  const handleBeforePrint = useCallback(() => {
    setIsPrinting(true);
    return Promise.resolve();
  }, []);

  const printFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `consulta_${document}`,
    onAfterPrint: handleAfterPrint,
    onBeforePrint: handleBeforePrint,
  });

  const {
    isLoading: isLoadingCustomQuery,
    error: customErrorQuery,
    data: customQuery,
  } = useQuery(["customQuery", query], async () => {
    const { data: items } = await axios.get(
      `${QUERIES.GET_QUERY}/${query?.id}`
    );
    return items.data;
  });

  useEffect(() => {
    setData(null);
    setInfo("");
    setError("");
    setErrorQuery(0);
    setDocument("");
    setShowMobileModal(false);
  }, [query]);

  useEffect(() => {
    if (dataFromLocalStorage) {
      setData(dataFromLocalStorage);
      setDocument(dataFromLocalStorage?.resumoRetorno?.document);
      setIsDoneRequest(true);
    }
  }, [query]);

  const fetchInfo = async (generatePDF = false, config = {}) => {
    const docValue =
      info || document || data?.resumoRetorno?.document || "";
    const docDigits = String(docValue).replace(/\D/g, "");
    const queryUrl = customQuery?.url || query?.url;

    if (!docDigits || !queryUrl) {
      throw new Error("missing-query-or-document");
    }

    return await axios(
      `${QUERIES.GET_V2_QUERY}/${queryUrl}/${docDigits}?generatePDF=${generatePDF}`,
      config
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setDocument(info);

    const unmaskedValue = info.replace(/\D/g, "");
    let isValid = true;

    if (customQuery?.QueryCustomized?.parameterType === "CPF") {
      if (!isValidCPF(unmaskedValue)) {
        setError("CPF inválido!");
        isValid = false;
      }
    } else if (customQuery?.QueryCustomized?.parameterType === "CNPJ") {
      if (!isValidCNPJ(unmaskedValue)) {
        setError("CNPJ inválido!");
        isValid = false;
      }
    }

    if (isValid) {
      try {
        setData(null);
        setIsLoading(true);
        setIsDoneRequest(false);
        setError("");
        setErrorQuery(0);

        // Chamada principal: fetchInfo
        const { data: dados } = await fetchInfo();
        setData(dados.data);
        setIsLoading(false);
      } catch (error) {
        const status = error?.response?.status || 500;
        setErrorQuery(status);
        setIsLoading(false);
      } finally {
        setIsDoneRequest(true);
      }
    } else {
      setData(null);
    }
  };

  const renderComponentByKey = (key, dataItem, document) => {
    switch (key) {
      case "cadastralPF":
        return <CadastralPFQuery data={dataItem} />;
      case "cadastralCompletoPF":
        return <CadastralCompletoPFValida data={dataItem} />;
      case "cadastralPremiumPF":
        return <CadastralPremiumPFQuery data={dataItem} />;
      case "cadastralPJ":
        return <CadastralPJQuery data={dataItem} />;
      case "cadastralPremiumPJ":
        return <CadastralPremiumPJQuery data={dataItem} />;
      case "participacaoEmEmpresas":
        return <ParticipacaoEmEmpresasQuery data={dataItem} />;
      default:
        return null;
    }
  };

  const savePDFFn = async () => {
    setIsPrinting(true);
    try {
      const response = await fetchInfo(true, { timeout: 45000 });
      const pdfBase64 = response?.data?.data?.resumoRetorno?.pdfBase64;
      const documentId = response?.data?.data?.resumoRetorno?.document;
      if (!pdfBase64) return;
      const blob = base64ToBlob(pdfBase64, "application/pdf");
      if (!blob) return;
      const fileName = `consulta_${documentId || "documento"}.pdf`;

      if (isMobile && navigator?.canShare) {
        try {
          const file = new File([blob], fileName, { type: "application/pdf" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: fileName,
            });
            return;
          }
        } catch {}
      }

      const url = URL.createObjectURL(blob);
      if (isMobile) {
        const newTab = window.open(url, "_blank", "noopener,noreferrer");
        if (!newTab) {
          window.location.href = url;
        }
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        return;
      }
      const link = window.document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.rel = "noopener";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setIsPrinting(false);
    }
  };

  const renderSortedData = () => {
    const itemsArray = Object.entries(data)
      .filter(([key, value]) => value && value.prioridade)
      .sort(([, aValue], [, bValue]) => aValue.prioridade - bValue.prioridade);

    return itemsArray?.map(([key, dataItem]) => (
      <React.Fragment key={key}>
        {renderComponentByKey(key, dataItem, document)}
      </React.Fragment>
    ));
  };

  const ResultContent = () => (
    <>
      {!isLoading && data && (
        <Card className="card-pdf p-5 relative overflow-hidden pdf-sheet">
          <HeaderQuery title={query?.name} />

          {renderSortedData()}

          <FooterLGPD />
        </Card>
      )}
    </>
  );

  return (
    <>
      <PreparePrint isPrinting={isPrinting} />
      <div className="relative">
        <CardQueryDescription
          title="Informações da consulta"
          price={query?.price}
          description={query?.description}
          tags={query?.tags}
        >
          <form
            className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-md w-full md:w-fit"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col md:flex-row gap-2 items-center z-10">
              <QueryInput
                info={info}
                setInfo={setInfo}
                isLoading={isLoading}
                error={error}
                type={customQuery?.QueryCustomized?.parameterType}
              />
            </div>
          </form>
          <div className="flex gap-2">
            {!errorQuery && data && (
              <Button className="bg-gray-600 hidden md:block" onClick={printFn}>
                <div className="flex gap-2 items-center">
                  <FaRegFilePdf className="hidden md:block" />
                  <div className="text-2xs md:text-sm">Salvar PDF</div>
                </div>
              </Button>
            )}
            {/* {!errorQuery && data && (
              <Button className="primary" onClick={savePDFFn}>
                <div className="flex gap-2 items-center">
                  <FaRegFilePdf className="hidden md:block" />
                  <div className="text-2xs md:text-sm whitespace-nowrap">
                    Salvar PDF
                  </div>
                </div>
              </Button>
            )} */}
          </div>
        </CardQueryDescription>
        {isLoadingCustomQuery && <DrawerLoading />}
      </div>

      {data && (
        <div className=" flex-col md:flex-row items-center bg-white p-2 gap-2 rounded-md shadow-md mb-4 mt-4 hidden md:flex">
          <div className="font-semibold text-sm">
            Deseja realizar uma nova consulta?
          </div>
          <Button
            onClick={() => {
              setData(null);
              setInfo("");
              setShowMobileModal(false);
            }}
          >
            Realizar nova consulta
          </Button>
        </div>
      )}

      {errorQuery ? (
        <ErrorQuery status={errorQuery} />
      ) : (
        <>
          <div
            ref={componentRef}
            className={`
              overflow-x-scroll print-content mobile-fit mx-auto scroll-mt-24
              ${data || isLoading ? "md:block hidden" : "hidden"}
            `}
          >
            <ResultContent />
          </div>

          {isMobile && (data || !isLoading) && (
            <Modal
              show={showMobileModal}
              onClose={() => setShowMobileModal(false)}
              size="7xl"
            >
              <ModalHeader className="py-2">
                {!errorQuery && data && (
                  <div className="ml-auto flex gap-2">
                    <Button size="xs" onClick={savePDFFn}>
                      <FaRegFilePdf className="mr-1" /> Salvar PDF
                    </Button>
                  </div>
                )}
              </ModalHeader>
              <ModalBody className="py-2 px-2">
                <div
                  className="overflow-x-hidden"
                  style={{ maxHeight: "480px" }}
                  ref={(el) => {
                    componentRef.current = el;
                    printRef.current = el;
                  }}
                >
                  <ResultContent />
                </div>
              </ModalBody>
            </Modal>
          )}

          {isLoading && (
            <ConsultaLoading
              fullscreen
              brandName="Relinq"
              accent="brand"
              tips={[
                "Autenticando provedores",
                "Buscando dados das fontes confiáveis",
                "Consolidando informações",
              ]}
            />
          )}
        </>
      )}
    </>
  );
};

export default CustomCadastralQuery;
